import { DateRangePicker, Input, Select } from '@aigc/antd';
import { Available, Datetime, NonEmpty, NumberRange } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { ExportOutlined, PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  App,
  Button,
  Form,
  Image,
  Popconfirm,
  Space,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  array,
  mixed,
  number,
  object,
  string,
  tuple,
  type InferType,
} from 'yup';
import { FormControl } from '../../components/form-control';
import { Permission } from '../../features/common/permision';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../features/file/utils';
import { HookTypeSelect } from '../../features/lookup/components/hook-type-select';
import { ScriptLanguageSelect } from '../../features/lookup/components/script-language-select';
import { SellingPointSelect } from '../../features/lookup/components/selling-point-select';
import { useAuthority } from '../../features/me/hook';
import {
  deleteScriptTemplateMutationOptions,
  getScriptTemplatesQueryOptions,
} from '../../features/script-template/api';
import { ScriptTemplateFormDrawer } from '../../features/script-template/components/script-template-form-drawer';
import type { ScriptTemplateResponse } from '../../features/script-template/model';
import { tagsQueryOptions } from '../../features/tag/api';
import { NavigateButton } from '../../features/video/components/navigate-button';

const query_schema = object({
  product_name: string().trim().nullable().default(null),
  tag_ids: array(string().trim().required()).nullable().default(null),
  created_at_range: mixed<[Date, Date]>().nullable().default(null),
  video_duration: tuple([number().nullable(), number().nullable()])
    .nullable()
    .default(null),
  hook_type_ids: array(number().required()).nullable().default(null),
  selling_point_ids: array(number().required()).nullable().default(null),
  script_language_ids: array(number().required()).nullable().default(null),
});

type QueryFormValues = InferType<typeof query_schema>;

export const Route = createFileRoute('/_creator-center/script-template')({
  staticData: { permissions: [Permission.视频生成] },
  component: ScriptTemplatePage,
});

function ScriptTemplatePage() {
  const { message } = App.useApp();
  const [open_overlay] = useOverlay<boolean>();
  const authority = useAuthority();

  const { control, handleSubmit, reset, getValues } = useForm({
    defaultValues: {
      product_name: null,
      tag_ids: null,
      created_at_range: null,
      video_duration: null,
      hook_type_ids: null,
      selling_point_ids: null,
      script_language_ids: null,
    },
    resolver: yupResolver(query_schema),
  });

  const { pagination, queryMany, queryParams, changePageNumber } =
    usePagingQueryHelper<QueryFormValues>({
      defaultPagination: { page: 1, size: 10 },
      defaultQueryParams: getValues(),
    });

  const { data: tags = [] } = useQuery(tagsQueryOptions);

  const { data: script_templates, isFetching: is_fetching } = useQuery({
    ...getScriptTemplatesQueryOptions(
      useMemo(() => {
        const [start_time, end_time] = queryParams?.created_at_range ?? [];
        const [duration_min, duration_max] = queryParams?.video_duration ?? [];

        return {
          page: pagination.page,
          size: pagination.size,
          product_name: queryParams?.product_name || null,
          tag_ids: queryParams?.tag_ids?.length ? queryParams.tag_ids : null,
          created_at_start: start_time?.toISOString() ?? null,
          created_at_end: end_time?.toISOString() ?? null,
          video_duration_min: duration_min ?? null,
          video_duration_max: duration_max ?? null,
          hook_type_ids: queryParams?.hook_type_ids?.length
            ? queryParams.hook_type_ids
            : null,
          selling_point_ids: queryParams?.selling_point_ids?.length
            ? queryParams.selling_point_ids
            : null,
          script_language_ids: queryParams?.script_language_ids?.length
            ? queryParams.script_language_ids
            : null,
        };
      }, [pagination.page, pagination.size, queryParams]),
    ),
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: delete_script_template, isPending: is_delete_pending } =
    useMutation(deleteScriptTemplateMutationOptions);

  const handleQuery = handleSubmit((values) => {
    queryMany(values);
  });

  const open_create_drawer = useCallback(() => {
    open_overlay(<ScriptTemplateFormDrawer />);
  }, [open_overlay]);

  const open_edit_drawer = useCallback(
    (script_template_id: number) => {
      open_overlay(
        <ScriptTemplateFormDrawer script_template_id={script_template_id} />,
      );
    },
    [open_overlay],
  );

  const columns = useMemo(() => {
    const _columns: TableColumnsType<ScriptTemplateResponse> = [
      {
        title: '视频',
        width: 192,
        render: (_, row) => {
          return (
            <div className="flex flex-col items-center">
              <video
                className="aspect-[9/16] w-[160px]"
                src={row.video_url}
                controls
              ></video>
              <Button
                classNames={{ root: 'mt-1' }}
                size="small"
                variant="text"
                color="primary"
                iconPlacement="end"
                icon={<ExportOutlined />}
                href={row.video_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                新标签页打开
              </Button>
            </div>
          );
        },
      },
      {
        title: '参考图',
        width: 304,
        render: (_, row) => {
          return (
            <div className="grid grid-cols-3 gap-2">
              {row.reference_image_urls?.map((url) => {
                return (
                  <Image
                    src={url}
                    width={80}
                    height={80}
                    classNames={{ image: 'object-cover' }}
                  ></Image>
                );
              })}
            </div>
          );
        },
      },
      {
        title: '产品名称',
        render: (_, row) => (
          <div className="line-clamp-3">{row.product_name}</div>
        ),
      },
      {
        title: '脚本信息',
        render: (_, row) => {
          return (
            <div>
              <div>
                时长：
                <NonEmpty
                  value={row.video_duration}
                  render={(value) => `${Math.round(value)}s`}
                  empty="未知"
                ></NonEmpty>
              </div>
              <div>
                宽高比：
                <NonEmpty
                  value={row.video_aspect_ratio}
                  render={(value) => {
                    return value;
                  }}
                  empty="未知"
                ></NonEmpty>
              </div>
              <div>
                标签：
                <NonEmpty
                  value={row.tags}
                  render={(value) => {
                    return (
                      <Space wrap>
                        {value.map((item) => (
                          <Tag key={item.id}>{item.name}</Tag>
                        ))}
                      </Space>
                    );
                  }}
                  empty="--"
                ></NonEmpty>
              </div>
              <div>
                Hook 类型：
                <NonEmpty
                  value={row.hook_type}
                  render={(value) => value.name}
                  empty="--"
                ></NonEmpty>
              </div>
              <div>
                卖点类型：
                <NonEmpty
                  value={row.selling_points}
                  render={(value) => (
                    <Space wrap>
                      {value.map((item) => (
                        <Tag key={item.id}>{item.name}</Tag>
                      ))}
                    </Space>
                  )}
                  empty="--"
                ></NonEmpty>
              </div>
              <div>
                脚本语言：
                <NonEmpty
                  value={row.script_language}
                  render={(value) => value.name}
                  empty="--"
                ></NonEmpty>
              </div>
            </div>
          );
        },
      },
      {
        title: '备注',
        render: (_, row) => <div className="line-clamp-3">{row.remark}</div>,
      },
      {
        title: '创建时间',
        width: 180,
        render: (_, row) => <Datetime value={row.created_at}></Datetime>,
      },
      {
        title: '最新修改时间',
        width: 180,
        render: (_, row) => <Datetime value={row.updated_at}></Datetime>,
      },
      {
        title: '操作',
        fixed: 'right',
        align: 'center',
        width: 160,
        render: (_, row) => (
          <Space orientation="vertical" align="center">
            <NavigateButton
              icon={null}
              variant="filled"
              color="default"
              size="small"
              script={{
                aspect_ratio: row.video_aspect_ratio!,
                duration: row.video_duration!,
                generation_type: null!,
                id: null!,
                template_id: row.id,
                prompt: row.prompt,
                file_list: toFileInputDtoList(
                  toFileListFormValues(
                    row.reference_image_urls ?? [],
                    'image/png',
                  ),
                ),
                product_name: row.product_name,
                source: void 0,
                generate_audio: true,
              }}
            ></NavigateButton>
            <Available when={() => authority(Permission.资产管理)}>
              <Button
                size="small"
                variant="filled"
                color="default"
                onClick={() => {
                  open_edit_drawer(row.id);
                }}
              >
                编辑
              </Button>
            </Available>
            <Available when={() => authority(Permission.资产管理)}>
              <Popconfirm
                title="删除脚本模版"
                description="删除后不可恢复，确认删除吗？"
                okText="删除"
                cancelText="取消"
                onConfirm={async () => {
                  await delete_script_template(row.id);
                  message.success('脚本模版已删除');
                }}
              >
                <Button
                  size="small"
                  variant="filled"
                  color="danger"
                  loading={is_delete_pending}
                >
                  删除
                </Button>
              </Popconfirm>
            </Available>
          </Space>
        ),
      },
    ];

    return _columns;
  }, [
    delete_script_template,
    is_delete_pending,
    message,
    open_edit_drawer,
    authority,
  ]);

  return (
    <div className="min-h-full bg-white p-4">
      <Form layout="vertical">
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-5">
          <FormControl
            name="product_name"
            control={control}
            label="产品名称"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} />
            )}
          />
          <FormControl
            name="tag_ids"
            control={control}
            label="标签"
            render={({ field }) => (
              <Select<string>
                mode="multiple"
                value={field.value ?? []}
                onChange={(values) => {
                  field.onChange(values?.length ? values : null);
                }}
                options={tags.map((item) => ({
                  label: item.name,
                  value: String(item.id),
                }))}
              />
            )}
          />
          <FormControl
            name="created_at_range"
            control={control}
            label="创建时间"
            render={({ field }) => (
              <DateRangePicker className="w-full" {...field} ref={field.ref} />
            )}
          />
          <FormControl
            control={control}
            name="video_duration"
            label="视频时长"
            render={({ field }) => (
              <NumberRange
                ref={field.ref}
                value={field.value ?? void 0}
                onChange={field.onChange}
                min={{ min: 0, precision: 0 }}
                max={{ min: 0, precision: 0 }}
              ></NumberRange>
            )}
          ></FormControl>
          <FormControl
            name="hook_type_ids"
            control={control}
            label="Hook 类型"
            render={({ field }) => (
              <HookTypeSelect
                mode="multiple"
                value={field.value ?? []}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl
            name="selling_point_ids"
            control={control}
            label="卖点类型"
            render={({ field }) => (
              <SellingPointSelect
                mode="multiple"
                value={field.value ?? []}
                onChange={field.onChange}
              />
            )}
          />
          <FormControl
            name="script_language_ids"
            control={control}
            label="脚本语言"
            render={({ field }) => (
              <ScriptLanguageSelect
                mode="multiple"
                value={field.value ?? []}
                onChange={field.onChange}
              />
            )}
          />
          <Form.Item label=" " colon={false}>
            <Space>
              <Button
                variant="outlined"
                color="default"
                onClick={() => {
                  reset();
                  queryMany(getValues());
                }}
              >
                重置
              </Button>
              <Button
                htmlType="submit"
                variant="filled"
                color="primary"
                onClick={handleQuery}
              >
                查询
              </Button>
            </Space>
          </Form.Item>
        </div>
      </Form>
      <Available when={() => authority(Permission.资产管理)}>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex-auto"></div>
          <Button
            variant="solid"
            color="primary"
            icon={<PlusOutlined />}
            onClick={open_create_drawer}
          >
            新建脚本模版
          </Button>
        </div>
      </Available>
      <Table<ScriptTemplateResponse>
        className="mt-4"
        rowKey={(row) => row.id}
        loading={is_fetching}
        columns={columns}
        dataSource={script_templates?.items ?? []}
        scroll={{ x: 1400 }}
        pagination={{
          current: script_templates?.page ?? pagination.page,
          pageSize: script_templates?.size ?? pagination.size,
          total: script_templates?.total ?? 0,
          onChange: (page, page_size) => {
            changePageNumber(page, page_size);
          },
        }}
      />
    </div>
  );
}
